import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ECRCode } from '../types/ecrCode';
import { Brand } from '../types/brand';
import { Label } from '../types/label';
import { useAuth } from '../auth/AuthContext';
import api from '../services/api';

interface DropdownData {
  contentUnit: any[];
  unit: any[];
  baseUnit: any[];
  baseComparisonUnitCode: any[];
  unitDescriptor: any[];
  country: any[];
  storageZone: any[];
  packagingUnit: any[];
  fangzone: any[];
  fangmethode: any[];
  line: any[];
  markantUnitDescriptor: any[];
  dangerousGoods: any[];
}

interface GlobalDataContextType {
  // External data
  ecrCodes: ECRCode[];
  brands: Brand[];
  labels: Label[];
  
  // Dropdown data
  dropdownData: DropdownData;
  
  // Loading states
  isLoading: boolean;
  isReady: boolean;
  error: string | null;
  
  // Reload function
  reloadData: () => Promise<void>;
}

const GlobalDataContext = createContext<GlobalDataContextType | undefined>(undefined);

interface GlobalDataProviderProps {
  children: ReactNode;
}

export const GlobalDataProvider: React.FC<GlobalDataProviderProps> = ({ children }) => {
  // Get authentication state - but handle supplier portal differently
  const { isAuthenticated: msalAuthenticated, isLoading: authLoading, authInitialized } = useAuth();
  
  // Check if we're in supplier portal
  const isSupplierPortal = window.location.pathname.includes('/supplier/');
  console.log('[GlobalData] Provider initialized, isSupplierPortal:', isSupplierPortal, 'path:', window.location.pathname);
  
  // For supplier portal, check session storage instead of MSAL
  const [supplierAuthenticated, setSupplierAuthenticated] = useState(false);
  const [supplierAuthLoading, setSupplierAuthLoading] = useState(true);

  useEffect(() => {
    if (isSupplierPortal) {
      // Check supplier authentication - this should re-run when sessionStorage changes
      const checkSupplierAuth = () => {
        const sessionToken = sessionStorage.getItem('supplierPortalSession');
        const sessionTime = sessionStorage.getItem('supplierPortalSessionTime');
        
        console.log('[GlobalData] Checking supplier authentication:', {
          hasSessionToken: !!sessionToken,
          hasSessionTime: !!sessionTime
        });
        
        if (sessionToken && sessionTime) {
          const sessionAge = Date.now() - parseInt(sessionTime);
          const maxSessionAge = 24 * 60 * 60 * 1000; // 24 hours
          
          if (sessionAge <= maxSessionAge) {
            console.log('[GlobalData] Supplier authenticated via session');
            setSupplierAuthenticated(true);
          } else {
            console.log('[GlobalData] Supplier session expired');
            setSupplierAuthenticated(false);
          }
        } else {
          console.log('[GlobalData] No valid supplier session found');
          setSupplierAuthenticated(false);
        }
        setSupplierAuthLoading(false);
      };

      // Initial check
      checkSupplierAuth();

      // FORCE IMMEDIATE CHECK - check every 1 second until authenticated
      const authCheckInterval = setInterval(() => {
        const hasSession = sessionStorage.getItem('supplierPortalSession');
        console.log('[GlobalData] Periodic auth check - hasSession:', !!hasSession, 'authenticated:', supplierAuthenticated);
        if (hasSession && !supplierAuthenticated) {
          console.log('[GlobalData] Found session but not authenticated, forcing auth check');
          checkSupplierAuth();
        } else if (!hasSession && supplierAuthenticated) {
          console.log('[GlobalData] No session but authenticated, clearing auth');
          setSupplierAuthenticated(false);
        } else if (hasSession && supplierAuthenticated) {
          console.log('[GlobalData] Auth stable, clearing interval');
          clearInterval(authCheckInterval);
        }
      }, 1000);
      
      // Clear interval after 10 seconds maximum
      setTimeout(() => clearInterval(authCheckInterval), 10000);

      // Listen for storage changes (when user logs in/out)
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'supplierPortalSession' || e.key === 'supplierPortalSessionTime') {
          console.log('[GlobalData] Supplier session storage changed, rechecking auth...');
          checkSupplierAuth();
        }
      };

      // Listen for custom events (when same tab updates sessionStorage)
      const handleSessionUpdate = () => {
        console.log('[GlobalData] Supplier session update event received, forcing immediate auth check and data reload');
        checkSupplierAuth();
        
        // Force data reload after a short delay
        setTimeout(() => {
          if (isSupplierPortal) {
            console.log('[GlobalData] Forcing data reload after session update');
            loadAllData();
          }
        }, 300);
      };

      window.addEventListener('storage', handleStorageChange);
      window.addEventListener('supplierSessionUpdate', handleSessionUpdate);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('supplierSessionUpdate', handleSessionUpdate);
      };
    }
  }, [isSupplierPortal]);

  // Determine which authentication system to use
  const isAuthenticated = isSupplierPortal ? supplierAuthenticated : msalAuthenticated;
  const authenticationLoading = isSupplierPortal ? supplierAuthLoading : authLoading;
  const authenticationInitialized = isSupplierPortal ? !supplierAuthLoading : authInitialized;
  
  console.log('[GlobalData] Authentication state:', {
    isSupplierPortal,
    supplierAuthenticated,
    supplierAuthLoading,
    msalAuthenticated,
    authLoading,
    authInitialized,
    finalAuth: isAuthenticated,
    finalLoading: authenticationLoading,
    finalInitialized: authenticationInitialized
  });
  
  // State for external data
  const [ecrCodes, setEcrCodes] = useState<ECRCode[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  
  // State for dropdown data
  const [dropdownData, setDropdownData] = useState<DropdownData>({
    contentUnit: [],
    unit: [],
    baseUnit: [],
    baseComparisonUnitCode: [],
    unitDescriptor: [],
    country: [],
    storageZone: [],
    packagingUnit: [],
    fangzone: [],
    fangmethode: [],
    line: [],
    markantUnitDescriptor: [],
    dangerousGoods: []
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAllData = async () => {
    console.log('[GlobalData] Starting to load all global data...');
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if we're in supplier portal context
      const isSupplierPortal = window.location.pathname.includes('/supplier/');
      console.log('[GlobalData] Loading data for:', isSupplierPortal ? 'Supplier Portal' : 'Admin Portal');
      
      if (isSupplierPortal) {
        // Supplier portal - load dropdown options via public API
        console.log('[GlobalData] Loading supplier-specific data...');
        
        try {
          // Load dropdown options from public API
          const sessionToken = sessionStorage.getItem('supplierPortalSession');
          const masterToken = window.location.pathname.split('/')[3];
          
          console.log('[GlobalData] Supplier portal auth tokens:', {
            hasSessionToken: !!sessionToken,
            hasMasterToken: !!masterToken,
            sessionTokenPreview: sessionToken ? sessionToken.substring(0, 10) + '...' : 'none',
            masterTokenPreview: masterToken ? masterToken.substring(0, 10) + '...' : 'none'
          });
          
          const dropdownResponse = await fetch('/api/public/portal/dropdown-options', {
            headers: {
              'x-session-token': sessionToken || '',
              'x-master-token': masterToken || ''
            }
          });
          
          let dropdownData: DropdownData = {
            contentUnit: [],
            unit: [],
            baseUnit: [],
            baseComparisonUnitCode: [],
            unitDescriptor: [],
            country: [],
            storageZone: [],
            packagingUnit: [],
            fangzone: [],
            fangmethode: [],
            line: [],
            markantUnitDescriptor: [],
            dangerousGoods: []
          };

          if (dropdownResponse.ok) {
            const dropdownResult = await dropdownResponse.json();
            console.log('[GlobalData] Dropdown options response:', dropdownResult);
            
            if (dropdownResult.success && dropdownResult.data) {
              // Group options by category
              const options = dropdownResult.data;
              
              dropdownData = {
                contentUnit: options.filter((opt: any) => opt.category === 'contentUnit'),
                unit: options.filter((opt: any) => opt.category === 'unit'),
                baseUnit: options.filter((opt: any) => opt.category === 'baseUnit'),
                baseComparisonUnitCode: options.filter((opt: any) => opt.category === 'baseComparisonUnitCode'),
                unitDescriptor: options.filter((opt: any) => opt.category === 'unitDescriptor'),
                country: options.filter((opt: any) => opt.category === 'country'),
                storageZone: options.filter((opt: any) => opt.category === 'storageZone'),
                packagingUnit: options.filter((opt: any) => opt.category === 'packagingUnit'),
                fangzone: options.filter((opt: any) => opt.category === 'fangzone'),
                fangmethode: options.filter((opt: any) => opt.category === 'fangmethode'),
                line: [], // Lines not needed in supplier portal
                markantUnitDescriptor: [], // Will be loaded separately
                dangerousGoods: options.filter((opt: any) => opt.category === 'dangerousGoods')
              };
              
              console.log('[GlobalData] Processed dropdown data for supplier portal:', {
                contentUnit: dropdownData.contentUnit.length,
                unit: dropdownData.unit.length,
                packagingUnit: dropdownData.packagingUnit.length,
                country: dropdownData.country.length
              });
            }
          } else {
            console.warn('[GlobalData] Failed to load dropdown options:', dropdownResponse.status);
          }

          setDropdownData(dropdownData);

          // Load ECR codes from public API
          try {
            console.log('[GlobalData] Loading ECR codes for supplier portal...');
            const ecrResponse = await fetch('/api/public/portal/ecr-codes', {
              headers: {
                'x-session-token': sessionToken || '',
                'x-master-token': masterToken || ''
              }
            });

            if (ecrResponse.ok) {
              const ecrResult = await ecrResponse.json();
              console.log('[GlobalData] ECR codes response:', {
                success: ecrResult.success,
                totalCodes: ecrResult.meta?.total || 0,
                hierarchyLength: ecrResult.data?.length || 0
              });
              
              if (ecrResult.success && ecrResult.data) {
                setEcrCodes(ecrResult.data);
                console.log('[GlobalData] ECR codes loaded for supplier portal:', ecrResult.data.length);
              } else {
                console.warn('[GlobalData] Invalid ECR codes response format');
                setEcrCodes([]);
              }
            } else {
              console.warn('[GlobalData] Failed to load ECR codes:', ecrResponse.status);
              setEcrCodes([]);
            }
          } catch (ecrError) {
            console.error('[GlobalData] Error loading ECR codes for supplier portal:', ecrError);
            setEcrCodes([]);
          }

          // Load brands from public API
          try {
            console.log('[GlobalData] Loading brands for supplier portal...');
            const brandsResponse = await fetch('/api/public/portal/brands', {
              headers: {
                'x-session-token': sessionToken || '',
                'x-master-token': masterToken || ''
              }
            });

            if (brandsResponse.ok) {
              const brandsResult = await brandsResponse.json();
              console.log('[GlobalData] Brands response:', {
                success: brandsResult.success,
                totalBrands: brandsResult.meta?.total || 0,
                brandsLength: brandsResult.data?.length || 0
              });
              
              if (brandsResult.success && brandsResult.data) {
                setBrands(brandsResult.data);
                console.log('[GlobalData] Brands loaded for supplier portal:', brandsResult.data.length);
              } else {
                console.warn('[GlobalData] Invalid brands response format');
                setBrands([]);
              }
            } else {
              console.warn('[GlobalData] Failed to load brands:', brandsResponse.status);
              setBrands([]);
            }
          } catch (brandsError) {
            console.error('[GlobalData] Error loading brands for supplier portal:', brandsError);
            setBrands([]);
          }

          // Load labels from public API
          try {
            console.log('[GlobalData] Loading labels for supplier portal...');
            const labelsResponse = await fetch('/api/public/portal/labels', {
              headers: {
                'x-session-token': sessionToken || '',
                'x-master-token': masterToken || ''
              }
            });

            if (labelsResponse.ok) {
              const labelsResult = await labelsResponse.json();
              console.log('[GlobalData] Labels response:', {
                success: labelsResult.success,
                totalLabels: labelsResult.meta?.total || 0,
                labelsLength: labelsResult.data?.length || 0
              });
              
              if (labelsResult.success && labelsResult.data) {
                setLabels(labelsResult.data);
                console.log('[GlobalData] Labels loaded for supplier portal:', labelsResult.data.length);
              } else {
                console.warn('[GlobalData] Invalid labels response format');
                setLabels([]);
              }
            } else {
              console.warn('[GlobalData] Failed to load labels:', labelsResponse.status);
              setLabels([]);
            }
          } catch (labelsError) {
            console.error('[GlobalData] Error loading labels for supplier portal:', labelsError);
            setLabels([]);
          }

          // Load lines from public API
          try {
            console.log('[GlobalData] Loading lines for supplier portal...');
            const linesResponse = await fetch('/api/public/portal/lines', {
              headers: {
                'x-session-token': sessionToken || '',
                'x-master-token': masterToken || ''
              }
            });

            if (linesResponse.ok) {
              const linesResult = await linesResponse.json();
              console.log('[GlobalData] Lines response:', {
                success: linesResult.success,
                totalLines: linesResult.meta?.total || 0,
                linesLength: linesResult.data?.length || 0
              });
              
              if (linesResult.success && linesResult.data) {
                // Transform lines data to match expected format
                const transformedLines = linesResult.data.map((line: any) => ({
                  _id: line._id,
                  _Value_DE: line._Value_DE,
                  value: line._Value_DE,
                  label: line._Value_DE
                }));
                
                setDropdownData(prevData => ({
                  ...prevData,
                  line: transformedLines
                }));
                
                console.log('[GlobalData] Lines loaded for supplier portal:', transformedLines.length);
              } else {
                console.warn('[GlobalData] Invalid lines response format');
                setDropdownData(prevData => ({ ...prevData, line: [] }));
              }
            } else {
              console.warn('[GlobalData] Failed to load lines:', linesResponse.status);
              setDropdownData(prevData => ({ ...prevData, line: [] }));
            }
          } catch (linesError) {
            console.error('[GlobalData] Error loading lines for supplier portal:', linesError);
            setDropdownData(prevData => ({ ...prevData, line: [] }));
          }

          // Load MarkantUnitDescriptor from public API
          try {
            console.log('[GlobalData] Loading markant unit descriptors for supplier portal...');
            const markantUnitDescriptorResponse = await fetch('/api/public/portal/markant-unit-descriptors', {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            });

            if (markantUnitDescriptorResponse.ok) {
              const markantUnitDescriptorResult = await markantUnitDescriptorResponse.json();
              console.log('[GlobalData] MarkantUnitDescriptor response:', {
                success: markantUnitDescriptorResult.success,
                totalDescriptors: markantUnitDescriptorResult.meta?.total || 0,
                descriptorsLength: markantUnitDescriptorResult.data?.length || 0
              });

              if (markantUnitDescriptorResult.success && markantUnitDescriptorResult.data) {
                // Transform MarkantUnitDescriptor data to match expected format
                const transformedDescriptors = markantUnitDescriptorResult.data.map((descriptor: any) => ({
                  _id: descriptor._id,
                  value: descriptor._Value_IND,
                  label_DE: descriptor._Value_DE,
                  isActive: descriptor.isActive
                }));
                
                setDropdownData(prevData => ({
                  ...prevData,
                  markantUnitDescriptor: transformedDescriptors
                }));
                
                console.log('[GlobalData] MarkantUnitDescriptors loaded for supplier portal:', transformedDescriptors.length);
              } else {
                console.warn('[GlobalData] Invalid MarkantUnitDescriptor response format');
                setDropdownData(prevData => ({ ...prevData, markantUnitDescriptor: [] }));
              }
            } else {
              console.warn('[GlobalData] Failed to load MarkantUnitDescriptors:', markantUnitDescriptorResponse.status);
              setDropdownData(prevData => ({ ...prevData, markantUnitDescriptor: [] }));
            }
          } catch (descriptorError) {
            console.error('[GlobalData] Error loading MarkantUnitDescriptors for supplier portal:', descriptorError);
            setDropdownData(prevData => ({ ...prevData, markantUnitDescriptor: [] }));
          }

        } catch (error) {
          console.error('[GlobalData] Error loading dropdown options for supplier portal:', error);
          // Set empty data on error
          setDropdownData({
            contentUnit: [],
            unit: [],
            baseUnit: [],
            baseComparisonUnitCode: [],
            unitDescriptor: [],
            country: [],
            storageZone: [],
            packagingUnit: [],
            fangzone: [],
            fangmethode: [],
            line: [],
            markantUnitDescriptor: [],
            dangerousGoods: []
          });
          setEcrCodes([]);
          setBrands([]);
          setLabels([]);
        }

        console.log('[GlobalData] Supplier data loaded successfully');
        
      } else {
        // Admin portal - load all data including admin-only resources
        console.log('[GlobalData] Loading admin data...');
        const [
          ecrCodesResult,
          brandsResult,
          labelsResult,
          contentUnitData,
          unitData,
          baseUnitData,
          baseComparisonUnitCodeData,
          unitDescriptorData,
          countryData,
          storageZoneData,
          packagingUnitData,
          fangzoneData,
          fangmethodeData,
          lineData,
          dangerousGoodsData
        ] = await Promise.allSettled([
          api.get('/ecr-codes').then(r => r.data),
          api.get('/brands').then(r => r.data),
          api.get('/labels').then(r => r.data),
          api.get('/dropdown-options/contentUnit').then(r => r.data),
          api.get('/dropdown-options/unit').then(r => r.data),
          api.get('/dropdown-options/baseUnit').then(r => r.data),
          api.get('/dropdown-options/baseComparisonUnitCode').then(r => r.data),
          api.get('/dropdown-options/unitDescriptor').then(r => r.data),
          api.get('/dropdown-options/country').then(r => r.data),
          api.get('/dropdown-options/storageZone').then(r => r.data),
          api.get('/dropdown-options/packagingUnit').then(r => r.data),
          api.get('/dropdown-options/fangzone').then(r => r.data),
          api.get('/dropdown-options/fangmethode').then(r => r.data),
          api.get('/lines').then(r => r.data),
          api.get('/dropdown-options/dangerousGoods').then(r => r.data)
        ]);

        // Handle external data for admin portal
        if (ecrCodesResult.status === 'fulfilled') {
          // Handle different response formats: admin API returns {data, total}, others may return array directly
          let ecrData;
          if (ecrCodesResult.value && typeof ecrCodesResult.value === 'object') {
            if (ecrCodesResult.value.data) {
              // Admin API format: {data: [...], total: number}
              ecrData = ecrCodesResult.value.data;
            } else if (Array.isArray(ecrCodesResult.value)) {
              // Direct array format
              ecrData = ecrCodesResult.value;
            } else {
              // Fallback
              ecrData = [];
            }
          } else {
            ecrData = [];
          }
          
          console.log('[GlobalData] ECR codes loaded for admin portal:', {
            rawResponseType: typeof ecrCodesResult.value,
            hasDataProperty: !!(ecrCodesResult.value?.data),
            finalDataLength: ecrData?.length || 0
          });
          
          setEcrCodes(ecrData);
        } else {
          console.error('[GlobalData] Failed to load ECR codes for admin portal:', ecrCodesResult.reason);
          setEcrCodes([]);
        }

        if (brandsResult.status === 'fulfilled') {
          // Handle different response formats: some APIs return array directly, others might return {data}
          let brandsData;
          if (brandsResult.value && Array.isArray(brandsResult.value)) {
            // Direct array format (brands API)
            brandsData = brandsResult.value;
          } else if (brandsResult.value?.data && Array.isArray(brandsResult.value.data)) {
            // Nested format (some other APIs)
            brandsData = brandsResult.value.data;
          } else {
            // Fallback
            brandsData = [];
          }
          
          console.log('[GlobalData] Brands loaded for admin portal:', {
            rawResponseType: typeof brandsResult.value,
            isArray: Array.isArray(brandsResult.value),
            hasDataProperty: !!(brandsResult.value?.data),
            finalDataLength: brandsData?.length || 0
          });
          
          setBrands(brandsData);
        } else {
          console.error('[GlobalData] Failed to load brands for admin portal:', brandsResult.reason);
          setBrands([]);
        }

        if (labelsResult.status === 'fulfilled') {
          // Handle different response formats: some APIs return array directly, others might return {data}
          let labelsData;
          if (labelsResult.value && Array.isArray(labelsResult.value)) {
            // Direct array format (labels API)
            labelsData = labelsResult.value;
          } else if (labelsResult.value?.data && Array.isArray(labelsResult.value.data)) {
            // Nested format (some other APIs)
            labelsData = labelsResult.value.data;
          } else {
            // Fallback
            labelsData = [];
          }
          
          console.log('[GlobalData] Labels loaded for admin portal:', {
            rawResponseType: typeof labelsResult.value,
            isArray: Array.isArray(labelsResult.value),
            hasDataProperty: !!(labelsResult.value?.data),
            finalDataLength: labelsData?.length || 0
          });
          
          setLabels(labelsData);
        } else {
          console.error('[GlobalData] Failed to load labels for admin portal:', labelsResult.reason);
          setLabels([]);
        }

        // Process dropdown data for admin portal
        const newDropdownData: DropdownData = {
          contentUnit: contentUnitData.status === 'fulfilled' ? contentUnitData.value.data || contentUnitData.value : [],
          unit: unitData.status === 'fulfilled' ? unitData.value.data || unitData.value : [],
          baseUnit: baseUnitData.status === 'fulfilled' ? baseUnitData.value.data || baseUnitData.value : [],
          baseComparisonUnitCode: baseComparisonUnitCodeData.status === 'fulfilled' ? baseComparisonUnitCodeData.value.data || baseComparisonUnitCodeData.value : [],
          unitDescriptor: unitDescriptorData.status === 'fulfilled' ? unitDescriptorData.value.data || unitDescriptorData.value : [],
          country: countryData.status === 'fulfilled' ? countryData.value.data || countryData.value : [],
          storageZone: storageZoneData.status === 'fulfilled' ? storageZoneData.value.data || storageZoneData.value : [],
          packagingUnit: packagingUnitData.status === 'fulfilled' ? packagingUnitData.value.data || packagingUnitData.value : [],
          fangzone: fangzoneData.status === 'fulfilled' ? fangzoneData.value.data || fangzoneData.value : [],
          fangmethode: fangmethodeData.status === 'fulfilled' ? fangmethodeData.value.data || fangmethodeData.value : [],
          line: lineData.status === 'fulfilled' ? lineData.value.data || lineData.value : [],
          markantUnitDescriptor: [], // MarkantUnitDescriptor not used in admin portal
          dangerousGoods: dangerousGoodsData.status === 'fulfilled' ? dangerousGoodsData.value.data || dangerousGoodsData.value : []
        };

        setDropdownData(newDropdownData);
        console.log('[GlobalData] Admin data loaded successfully');
        console.log('[GlobalData] Dropdown data:', newDropdownData);
        console.log('[GlobalData] Unit options count:', newDropdownData.unit.length);
      }

      setIsReady(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('[GlobalData] Error loading data:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only load data when user is authenticated, auth is initialized and not loading
    console.log('[GlobalData] useEffect triggered with:', { 
      isAuthenticated, 
      authenticationInitialized, 
      authenticationLoading,
      isSupplierPortal,
      pathname: window.location.pathname
    });
    
    if (isAuthenticated && authenticationInitialized && !authenticationLoading) {
      console.log('[GlobalData] User authenticated and fully ready, loading data...');
      // Add small delay to ensure tokens are fully available
      const timer = setTimeout(() => {
        loadAllData();
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      console.log('[GlobalData] User not ready yet, waiting...', { 
        isAuthenticated, 
        authenticationInitialized, 
        authenticationLoading,
        isSupplierPortal
      });
      
      // Reset ready state when not authenticated
      if (!isAuthenticated && isReady) {
        console.log('[GlobalData] User no longer authenticated, resetting ready state');
        setIsReady(false);
      }
    }
  }, [isAuthenticated, authenticationLoading, authenticationInitialized, isSupplierPortal]);

  // Separate useEffect for supplier session updates to ensure immediate reloading
  useEffect(() => {
    if (isSupplierPortal && supplierAuthenticated && !supplierAuthLoading) {
      console.log('[GlobalData] Supplier authentication status changed, forcing data reload...');
      // Longer delay for supplier portal to ensure session is properly established
      const timer = setTimeout(() => {
        loadAllData();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [supplierAuthenticated, supplierAuthLoading, isSupplierPortal]);

  const contextValue: GlobalDataContextType = {
    ecrCodes,
    brands,
    labels,
    dropdownData,
    isLoading,
    isReady,
    error,
    reloadData: loadAllData
  };

  // Debug logging when context values change
  useEffect(() => {
    console.log('[GlobalData] Context values updated:', {
      ecrCodesCount: ecrCodes?.length || 0,
      brandsCount: brands?.length || 0,
      labelsCount: labels?.length || 0,
      dropdownDataCategories: Object.keys(dropdownData),
      isLoading,
      isReady,
      hasError: !!error
    });
  }, [ecrCodes, brands, labels, dropdownData, isLoading, isReady, error]);

  return (
    <GlobalDataContext.Provider value={contextValue}>
      {children}
    </GlobalDataContext.Provider>
  );
};

export const useGlobalData = () => {
  const context = useContext(GlobalDataContext);
  if (!context) {
    throw new Error('useGlobalData must be used within a GlobalDataProvider');
  }
  return context;
};

export default GlobalDataContext;
